import React, { useRef, useState } from "react";
import { Button, Box, Typography } from "@mui/material";
import { toast } from "react-toastify";

interface Props {
  value: File | null;
  onChange: (file: File | null) => void;
}

const ImageUpload: React.FC<Props> = ({ value, onChange }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file.", {
        position: "top-right",
        autoClose: 3000,
      });
      e.target.value = "";
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`, {
        position: "top-right",
        autoClose: 3000,
      });
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
    onChange(file);
  };

  const handleRemoveImage = () => {
    setPreview(null);
    onChange(null);
    if (uploadRef.current) {
      uploadRef.current.value = "";
    }
  };

  // Get preview URL from value prop if preview state is not set
  const getPreviewUrl = () => {
    if (preview) return preview;
    if (value instanceof File) return URL.createObjectURL(value);
    return null;
  };

  const previewUrl = getPreviewUrl();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <input type="file" accept="image/*" ref={uploadRef} onChange={handleFile} hidden />

        <Button variant="outlined" onClick={() => uploadRef.current?.click()}>
          Upload Image
        </Button>

        {previewUrl && (
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: 50,
                height: 50,
                objectFit: "cover",
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            />

            {/* Remove button */}
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                minWidth: "auto",
                width: 20,
                height: 20,
                borderRadius: "50%",
                fontSize: "12px",
                p: 0,
              }}
            >
              ×
            </Button>
          </Box>
        )}
      </Box>

      {/* File details section */}
      {previewUrl && value && (
        <Box sx={{ pl: 0 }}>
          {/* <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            Image Preview:
          </Typography> */}
          <Typography variant="caption" color="text.secondary">
            File: {value.name} ({(value.size / (1024 * 1024)).toFixed(2)} MB)
          </Typography>
        </Box>
      )}

      {/* Maximum file size info - always visible */}
      <Typography variant="caption" color="text.secondary">
        Maximum file size: {MAX_FILE_SIZE_MB}MB
      </Typography>
    </Box>
  );
};

export default ImageUpload;
